// @flow
import Debug from 'debug';
import Select from 'ol/interaction/Select';
import type { Feature } from '../modules/ui/openlayers';
import { Zone, Target } from '../types';
import { createGeometry as createZoneGeometry } from '../utils/zoneUtils';
import { createGeometry as createTargetGeometry } from '../utils/targetUtils';

const debug = Debug('amms:services:map');

export const defaultPadding = 500;

type LocateOptions = $ReadOnly<{
    duration?: number,
    padding?: [number, number, number, number]
}>

const mapService = {
    _map: null,

    hasMap: () => Boolean(mapService.map),

    getMap: () => {
        if(!mapService.map) {
            throw new Error('Map is not set');
        }

        return mapService.map;
    },

    setMap: (map) => {
        debug('Setting map: ', map);
        mapService.map = map;
    },

    locateGeometry: (geometry, options?: LocateOptions = {}) => {
        const map = mapService.getMap();
        const view = map.getView();

        const {
            duration = 500,
            padding = [defaultPadding, defaultPadding, defaultPadding, defaultPadding]
        } = options;

        view.fit(geometry, {
            padding: padding,
            maxZoom: 17,
            duration: duration
        });
    },

    locateZone: (zone: Zone, options?: LocateOptions) => {
        debug('Locating zone: ', zone);

        const feature = zone.feature;
        const geometry = createZoneGeometry(feature.type, feature);

        mapService.locateGeometry(geometry, options);
    },

    selectZones: (zoneIds: $ReadOnlyArray<string>) => {
        mapService.deselectAll();

        const map = mapService.getMap();
        const layer = map.getLayers().getArray().find((layer => layer.get('label') === 'zones'));
        const source = layer && layer.getSource();
        const features = source && source.getFeatures().filter(feature => zoneIds.indexOf(feature.get('id')) !== -1);
        const interaction = map.getInteractions().getArray().find(interaction => interaction.get('label') === 'selectZones');

        if(features && features.length !== 0 && interaction) {
            const featuresCollection = interaction.getFeatures();

            for(let i = 0, length = features.length; i < length; i += 1) {
                featuresCollection.push(features[i]);
            }
        }
    },

    locateTarget: (target: Target, options?: LocateOptions) => {
        debug('Locating target: ', target);

        const geometry = createTargetGeometry(target);

        mapService.locateGeometry(geometry, options);
    },

    selectTarget: (target: Target) => {
        mapService.deselectAll();

        const map = mapService.getMap();
        const layer = map.getLayers().getArray().find((layer => layer.get('label') === 'targets'));
        const source = layer && layer.getSource();
        const feature = source && source.getFeatures().find(feature => feature.get('targetType') === target.type && feature.get('icao') === target.Icao);
        const interaction = map.getInteractions().getArray().find(interaction => interaction.get('label') === 'targets');

        if(feature && interaction) {
            const features = interaction.getFeatures();
            features.push(feature);
        }
    },

    deselectAll: (type?: string) => {
        const map = mapService.getMap();
        const interactions = map.getInteractions().getArray().filter(interaction => interaction instanceof Select);

        for(let i = 0, length = interactions.length; i < length; i += 1) {
            const features = interactions[i].getFeatures();

            if(type) {
                const featureArray = features.getArray();
                const featuresToRemove = [];
                for(let j = 0, featureLength = featureArray.length; j < featureLength; j += 1) {
                    const feature = featureArray[j];

                    if(feature.get('type') === type) {
                        featuresToRemove.push(feature);
                    }
                }

                for(let j = 0, featureLength = featuresToRemove.length; j < featureLength; j += 1) {
                    const feature = featuresToRemove[j];
                    features.remove(feature);
                }
            }
            else {
                features.clear();
            }

        }
    },

    hasSelected: (filter?: (feature: Feature) => boolean) => {
        const map = mapService.getMap();
        const interactions = map.getInteractions().getArray().filter(interaction => interaction instanceof Select);

        for(let i = 0, length = interactions.length; i < length; i += 1) {
            const features = interactions[i].getFeatures();
            if(features.getLength() > 0 && (!filter || features.getArray().some(filter)) ) {
                return true;
            }
        }

        return false;
    }
};

export default mapService;